import type {Listing, ListingChannel} from "@hosttools/frontend/models/listing";
import sortBy from "lodash/sortBy";
import type React from "react";
import type {ReactElement} from "react";
import {memo, useCallback, useEffect, useState} from "react";

import LockListingSelect from "@/client/components/LockListingSelect";

type ListingOption = {
    value: string;
    label: string;
    source?: ListingChannel;
    Icon?: ReactElement;
    listing: Listing;
};

export type Props = {
    listings: Listing[];
    listing: Listing;
    field: "linkedRoomID";
    disabled?: boolean;
    onChange: (selectedListing: Listing[]) => void;
    onAdd?: (listing: Listing) => void;
    onRemove?: (listing: Listing) => void;
};

const SelectListings: React.FC<Props> = ({
    listing,
    listings,
    field,
    disabled,
    onChange,
    onAdd,
    onRemove
}) => {
    const [selectListings, setSelectListings] = useState<Listing[]>();
    const [listingOptions, setListingOptions] = useState<ListingOption[]>([]);

    const buildListingOptions = useCallback(
        (selectListings: Listing[]) => {
            const listingID = listing._id;
            const selectedListingIDs = selectListings.map(listing => listing._id);

            let options = listings.reduce<ListingOption[]>((result, listing) => {
                // Don't include this listing
                if (listingID === listing._id) {
                    return result;
                }

                // Don't allow listings that are hidden
                if (listing.hidden) {
                    return result;
                }

                // check compare to already selected listing and don't include listings that are already selected
                const isSelectedListing = listing._id && selectedListingIDs.includes(listing._id);
                if (!isSelectedListing) {
                    result.push({
                        value: listing._id,
                        label: listing.name,
                        listing
                    });
                }

                return result;
            }, []);

            options = sortBy(options, option => option.label.toLowerCase());
            setListingOptions(options);
        },
        [listing, listings]
    );

    const setNewSelectListings = useCallback(
        (selectListings: Listing[]) => {
            const newSelectListings = sortBy(selectListings, listing => {
                const listingName = listing.name;
                return listingName.toLowerCase();
            });
            setSelectListings(newSelectListings);
            onChange(newSelectListings);
        },
        [onChange]
    );

    const deleteSelectedListingID = useCallback(
        (removingListing: Listing) => {
            if (!selectListings) {
                return;
            }
            const filteredSelectListings = selectListings.filter(selectedListing => {
                return selectedListing._id !== removingListing._id;
            });

            buildListingOptions(filteredSelectListings);
            setNewSelectListings(filteredSelectListings);
            onRemove?.(removingListing);
        },
        [selectListings, buildListingOptions, setNewSelectListings, onRemove]
    );

    const handleSelectedOption = useCallback(
        (option: ListingOption | null) => {
            if (!option) {
                return;
            }
            if (!selectListings) {
                return;
            }
            const newListing = listings.find(listing => {
                return option.value === listing._id;
            });
            // Make sure the listing isn't already on the selectListings list
            if (selectListings.some(selectedListing => newListing?._id === selectedListing._id)) {
                return;
            }
            if (newListing) {
                selectListings.push(newListing);
                onAdd?.(newListing);
            }

            buildListingOptions(selectListings);
            setNewSelectListings(selectListings);
        },
        [selectListings, listings, buildListingOptions, setNewSelectListings, onAdd]
    );

    useEffect(() => {
        const listingID = listing._id;
        const selectListings = listings.reduce<Listing[]>((result, listing) => {
            if (listing[field] === listingID) {
                result.push(listing);
            }
            return result;
        }, []);
        buildListingOptions(selectListings);
        setNewSelectListings(selectListings);
    }, [buildListingOptions, listing._id, listings, setNewSelectListings, field]);

    return (
        <LockListingSelect
            disabled={disabled}
            options={listingOptions}
            selectedItems={selectListings}
            onRemoveItem={deleteSelectedListingID}
            onSelectOption={handleSelectedOption}
        />
    );
};

export default memo(SelectListings);
